import { useState, useCallback, useEffect } from 'react';
import { createChatProvider } from '../providers';
import type { ChatProvider, ChatState } from '../providers';

export interface UseLiveChatReturn extends ChatState {
  connect: (token: string, conversationId: string) => Promise<void>;
  sendMessage: (text: string, attributes?: Record<string, unknown>) => Promise<void>;
  disconnect: () => void;
}

/**
 * Owns one chat provider — built on the first render, disconnected on unmount.
 *
 * Call it exactly once, from LiveSessionProvider; screens read the conversation it
 * holds through useChatSession(). See useLiveVideo for the reasoning.
 */
export function useLiveChat(): UseLiveChatReturn {
  // Built on the first render, for the same reason as in useLiveVideo: a consumer's
  // mount effect runs before this hook's, and it must find a provider already there.
  const [provider] = useState<ChatProvider>(createChatProvider);

  const [state, setState] = useState<ChatState>({
    isConnected: false,
    isConnecting: false,
    messages: [],
    error: null,
    historyLoaded: false,
  });

  // Subscribe to the provider built above, and own its teardown.
  useEffect(() => {
    const unsubscribe = provider.subscribe(setState);
    // Same catch-up as in useLiveVideo: a consumer can have called connect()
    // before this subscription existed.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState(provider.getState());

    return () => {
      unsubscribe();
      provider.disconnect();
    };
  }, [provider]);

  const connect = useCallback(async (token: string, conversationId: string) => {
    await provider.connect(token, conversationId);
  }, [provider]);

  const sendMessage = useCallback(async (text: string, attributes?: Record<string, unknown>) => {
    await provider.sendMessage(text, attributes);
  }, [provider]);

  const disconnect = useCallback(() => {
    provider.disconnect();
  }, [provider]);

  return {
    ...state,
    connect,
    sendMessage,
    disconnect,
  };
}
